import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { FaMapMarkerAlt, FaClock, FaChevronDown } from 'react-icons/fa';
import './ContactUsCard.css'; // Import CSS file
import { FaPhoneVolume } from 'react-icons/fa6';
import { MdOutlineEmail } from 'react-icons/md';

const ContactUsCard = ({ units }) => {
    const { t } = useTranslation(); // Initialize translation
    const [expandedHours, setExpandedHours] = useState({});

    const LOCATION_TYPES = {
        headquarters: { color: '#FFB800', label: 'Headquarters' },
        subsidiaries: { color: '#DC2626', label: 'Subsidiaries' },
        division: { color: '#1E3A8A', label: 'BU' },
        // partner: { color: '#059669', label: 'Partner' }
    };

    const toggleHours = (index) => {
        setExpandedHours(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const getOpeningStatus = (openingHours) => {
        // Get current time in Belgium
        const now = new Date();
        const belgiumTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Brussels' }));

        const currentDay = belgiumTime.getDay(); // 0 is Sunday, 1 is Monday, etc.
        const currentHour = belgiumTime.getHours();
        const currentMinute = belgiumTime.getMinutes();
        const currentTime = currentHour * 60 + currentMinute; // Convert to minutes

        // If it's weekend
        if (currentDay === 0 || currentDay === 6) {
            const daysUntilMonday = currentDay === 0 ? 1 : 2;
            const minutesUntilMonday = (daysUntilMonday * 24 * 60) - currentTime + (8 * 60);
            const hours = Math.floor(minutesUntilMonday / 60);
            const minutes = minutesUntilMonday % 60;
            return {
                isOpen: false,
                message: `${t("Opens in")} ${hours} ${t("hours and")} ${minutes} ${t("minutes")}`
            };
        }

        const isFriday = currentDay === 5;
        const closingTime = isFriday ? 16 * 60 : 17 * 60; // Convert to minutes
        const openingTime = 8 * 60; // 8:00 in minutes

        if (currentTime < openingTime) {
            const minutesUntilOpen = openingTime - currentTime;
            const hours = Math.floor(minutesUntilOpen / 60);
            const minutes = minutesUntilOpen % 60;
            return {
                isOpen: false,
                message: `${t("Opens in")} ${hours} ${t("hours and")} ${minutes} ${t("minutes")}`
            };
        } else if (currentTime >= closingTime) {
            if (isFriday) {
                const minutesUntilMonday = ((3 * 24 * 60) - currentTime) + (8 * 60);
                const hours = Math.floor(minutesUntilMonday / 60);
                const minutes = minutesUntilMonday % 60;
                return {
                    isOpen: false,
                    message: `${t("Opens in")} ${hours} ${t("hours and")} ${minutes} ${t("minutes")}`
                };
            }
            const minutesUntilOpen = (24 * 60 - currentTime) + 8 * 60;
            const hours = Math.floor(minutesUntilOpen / 60);
            const minutes = minutesUntilOpen % 60;
            return {
                isOpen: false,
                message: `${t("Opens in")} ${hours} ${t("hours and")} ${minutes} ${t("minutes")}`
            };
        } else {
            const minutesUntilClose = closingTime - currentTime;
            const hours = Math.floor(minutesUntilClose / 60);
            const minutes = minutesUntilClose % 60;
            return {
                isOpen: true,
                message: `${t("Closes in")} ${hours} ${t("hours and")} ${minutes} ${t("minutes")}`
            };
        }
    };

    return (
        <>
            <div className="legends-container">
                {Object.entries(LOCATION_TYPES).map(([type, { color, label }]) => (
                    <div key={type} className="legend-item">
                        <FaMapMarkerAlt style={{ color }} />
                        <span>{t(label)}</span>
                    </div>
                ))}
            </div>
            <div className="cards-container">
                {units.map((unit, index) => {
                    const locationColor = LOCATION_TYPES[unit.type]?.color || '#2188ED';
                    const openingStatus = getOpeningStatus(unit.openingHours);
                    const isExpanded = expandedHours[index];

                    return (
                        <div
                            key={index}
                            className={`contact-card ${unit.type}`}
                            style={{
                                backgroundImage: `url(${unit.image})`,
                            }}
                        >
                            <div className="content">
                                <div className="header">
                                    <h2 className="unit-name">{t(unit.name)}</h2>
                                    {unit.description && <h6 className="description">{t(unit.description)}</h6>}
                                </div>

                                <div className="info">
                                    <div className="info-section">
                                        <div className="info-item">
                                            <FaMapMarkerAlt
                                                className="icon"
                                                style={{ color: locationColor }}
                                            />
                                            <a
                                                className="text"
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(unit.coordinates)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ '--hover-color': locationColor }}
                                            >
                                                {t(unit.location)}
                                            </a>
                                        </div>
                                    </div>

                                    <div className="info-section">
                                        <div className="info-item contact-details">
                                            <a
                                                className="text"
                                                href={`tel:${unit.telephone.replace(/\s+/g, '')}`}
                                                style={{ '--hover-color': locationColor }}
                                            >
                                                <FaPhoneVolume /> {t(unit.telephone)}
                                            </a>
                                            {unit.telephone2 && (
                                                <a
                                                    className="text"
                                                    href={`tel:${unit.telephone2.replace(/\s+/g, '')}`}
                                                    style={{ '--hover-color': locationColor }}
                                                >
                                                    <FaPhoneVolume /> {t(unit.telephone2)}
                                                </a>
                                            )}
                                            {unit.telephone3 && (
                                                <a
                                                    className="text"
                                                    href={`tel:${unit.telephone3.replace(/\s+/g, '')}`}
                                                    style={{ '--hover-color': locationColor }}
                                                >
                                                    <FaPhoneVolume /> {t(unit.telephone3)}
                                                </a>
                                            )}
                                            {unit.fax && (
                                                <a
                                                    className="text"
                                                    href={`tel:${unit.fax.replace(/\s+/g, '')}`}
                                                    style={{ '--hover-color': locationColor }}
                                                >
                                                    {t(unit.fax)}
                                                </a>
                                            )}
                                            {unit.mail && (
                                                <a
                                                    className="text"
                                                    href={`mailto:${unit.mail}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ '--hover-color': locationColor }}
                                                >
                                                    <MdOutlineEmail /> {t(unit.mail)}
                                                </a>
                                            )}
                                            {unit.mail2 && (
                                                <a
                                                    className="text"
                                                    href={`mailto:${unit.mail2}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ '--hover-color': locationColor }}
                                                >
                                                    <MdOutlineEmail /> {t(unit.mail2)}
                                                </a>
                                            )}
                                        </div>
                                        <div className='info-item contact-details pt-2' style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                            {unit.service24h && (
                                                <a
                                                    className="text"
                                                    href={`tel:${unit.service24h.replace(/\s+/g, '')}`}
                                                    style={{ '--hover-color': locationColor }}
                                                >
                                                    Service 24hr: {t(unit.service24h)}
                                                </a>
                                            )}
                                            {unit.flexpress24h && (
                                                <a
                                                    className="text"
                                                    href={`tel:${unit.flexpress24h.replace(/\s+/g, '')}`}
                                                    style={{ '--hover-color': locationColor }}
                                                >
                                                    FLEXXPRESS 24h: {t(unit.flexpress24h)}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default ContactUsCard;
